import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Memory } from '@/types';
import { api } from '@/lib/api';
import { MEMORY } from '@/content/memory';
import WorldScene from '@/components/world/WorldScene';
import MemoryForm from './MemoryForm';

interface Props { source: string; }

/**
 * The entry band — a poster block on Home + /eternity. A quiet ETERNITY skyline
 * backs it (so it reads as a distinct band, not another flat panel, and previews
 * the world the full wall lives in), under a legibility shade. Eyebrow slate →
 * DIN heading → intro → form → a "just left" preview → link to the full wall.
 */
const MemoryBand = ({ source }: Props) => {
  const [recent, setRecent] = useState<Memory[]>([]);
  useEffect(() => {
    let cancelled = false;
    api.getMemories().then((r) => { if (!cancelled && r.data) setRecent(r.data.memories.slice(0, 3)); });
    return () => { cancelled = true; };
  }, []);

  return (
    <section className="mw-band" aria-label="Leave a memory">
      <div className="mw-band-bg" aria-hidden="true"><WorldScene intensity="calm" /></div>
      <div className="mw-band-shade" aria-hidden="true" />

      <div className="mw-band-inner">
        <p className="mw-band-slate"><span className="mw-band-rule" aria-hidden="true" />THE MEMORY WALL</p>
        <h2 className="mw-band-h">{MEMORY.bandHeading}</h2>
        <p className="mw-band-sub">{MEMORY.bandSub}</p>

        <div className="mw-band-form">
          <MemoryForm source={source} onLeft={(m) => setRecent((r) => [m, ...r].slice(0, 3))} />
        </div>

        {recent.length > 0 && (
          <div className="mw-band-recent">
            <p className="mw-band-rl">{MEMORY.recentLabel}</p>
            <ul className="mw-band-list">
              {recent.map((m) => (
                <li key={m.id} className="mw-band-r">
                  <span className="mw-band-q">{m.text}</span>
                  {m.initials && <span className="mw-band-i">— {m.initials}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}

        <Link to="/memory" className="mw-band-view">{MEMORY.viewWall}</Link>
      </div>

      <style>{`
        .mw-band { position:relative; overflow:hidden; isolation:isolate;
          padding: clamp(5rem,10vw,8rem) var(--bx, 2rem);
          border-top:1px solid var(--et-border); border-bottom:1px solid var(--et-border); }
        /* quiet ETERNITY skyline backdrop — what sets this band apart from neighbours */
        .mw-band-bg { position:absolute; inset:0; z-index:0; }
        .mw-band-shade { position:absolute; inset:0; z-index:1; pointer-events:none;
          background: linear-gradient(180deg,
            rgba(3,4,12,0.86) 0%, rgba(4,5,14,0.66) 48%, rgba(3,4,12,0.9) 100%); }

        .mw-band-inner { position:relative; z-index:2; max-width:58rem; margin:0 auto; }

        .mw-band-slate { display:flex; align-items:center; gap:1rem; margin:0 0 1.6rem;
          font-family:var(--font-mono); font-size:.66rem; font-weight:600; letter-spacing:.34em;
          text-transform:uppercase; color:var(--et-muted); }
        .mw-band-rule { width:2.6rem; height:1px; background:var(--et-cyan); }

        .mw-band-h { font-family:var(--font-display); font-weight:700; text-transform:uppercase;
          font-size:clamp(2.8rem,7vw,6rem); line-height:.88; letter-spacing:.005em;
          color:var(--et-text); margin:0; white-space:pre-line;
          text-shadow:0 2px 30px rgba(2,3,10,.7); }
        .mw-band-sub { color:var(--et-text); opacity:.78; font-size:clamp(1rem,1.5vw,1.18rem);
          line-height:1.5; max-width:34rem; margin:1.3rem 0 2.6rem; }
        .mw-band-form { max-width:40rem; }

        .mw-band-recent { margin-top:3rem; padding-top:1.8rem; border-top:1px solid var(--et-border); }
        .mw-band-rl { display:flex; align-items:center; gap:.5rem; margin:0 0 1.1rem;
          font-family:var(--font-mono); font-size:.58rem; font-weight:600; letter-spacing:.26em;
          text-transform:uppercase; color:var(--et-muted); }
        .mw-band-rl::before { content:''; width:5px; height:5px; border-radius:50%; background:var(--et-cyan); }
        .mw-band-list { list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:.85rem; }
        .mw-band-r { display:flex; align-items:baseline; gap:.7rem; padding-left:1rem;
          border-left:2px solid var(--et-border); font-family:var(--font-family); font-size:1.02rem;
          line-height:1.45; color:var(--et-text); }
        .mw-band-q { opacity:.94; }
        .mw-band-q::before { content:'\\201C'; } .mw-band-q::after { content:'\\201D'; }
        .mw-band-i { font-family:var(--font-mono); font-size:.62rem; letter-spacing:.14em;
          color:var(--et-cyan); white-space:nowrap; flex-shrink:0; }

        .mw-band-view { display:inline-flex; align-items:center; gap:.5rem; margin-top:2.6rem;
          font-family:var(--font-mono); font-size:.72rem; font-weight:700; letter-spacing:.22em;
          text-transform:uppercase; color:var(--et-cyan); border-bottom:1px solid transparent;
          padding-bottom:.25rem; transition:color var(--transition-fast), border-color var(--transition-fast); }
        .mw-band-view:hover { color:var(--et-text); border-color:var(--et-text); }
      `}</style>
    </section>
  );
};

export default MemoryBand;
